import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { getFeedbackCategories, submitFeedback } from "../../../api/agentic/feedbackApi";

const FEEDBACK_SUCCESS_TOAST_ID = "agentic-feedback-submit-success";
const FEEDBACK_ERROR_TOAST_ID = "agentic-feedback-submit-error";

const AGENT_SHORT_NAME_MAP = {
  "Document Quality Agent": "DQA",
  "Intake Review Agent": "IRA",
  "Benefits Agent": "BENEFITS",
  "Eligibility Review Agent": "ERA",
  "Application Status Agent": "ASA",
};

const FEEDBACK_DETAILS_CHAR_LIMIT = 300;

const toSafeStorageSegment = (value, fallback) => (value || fallback).replace(/\s+/g, "-").toLowerCase();

const FeedbackToggle = ({
  defaultValue = null,
  onChange,
  userId,
  agentName,
  threadId,
  ratedMessage,
  messageIndex,
  chatHistory,
  userPrompt,
}) => {
  const [feedback, setFeedback] = useState(defaultValue || ""); // 'like' | 'dislike' | ''
  const [showDislikePopup, setShowDislikePopup] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [details, setDetails] = useState("");
  const [issues, setIssues] = useState([]);
  const [isIssuesLoading, setIsIssuesLoading] = useState(false);
  const [hasLoadedCategories, setHasLoadedCategories] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedAgentName = AGENT_SHORT_NAME_MAP[agentName] || agentName || "Unknown";
  const safeUser = toSafeStorageSegment(userId || "unknown-user", "unknown-user");
  const safeAgent = toSafeStorageSegment(agentName || "unknown-agent", "unknown-agent");
  const storageThreadKey = `agentic-thread:${safeUser}:${safeAgent}`;
  const storageConversationKey = `agentic-conversation:${safeUser}:${safeAgent}`;

  useEffect(() => {
    if (!showDislikePopup) return undefined;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setShowDislikePopup(false);
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [showDislikePopup]);

  useEffect(() => {
    if (!showDislikePopup) return;
    if (hasLoadedCategories) return;

    let active = true;
    setIsIssuesLoading(true);

    getFeedbackCategories()
      .then((categories) => {
        if (!active) return;
        setIssues(Array.isArray(categories) ? categories : []);
      })
      .catch(() => {
        if (!active) return;
        setIssues([]);
      })
      .finally(() => {
        if (active) {
          setIsIssuesLoading(false);
          setHasLoadedCategories(true);
        }
      });

    return () => {
      active = false;
    };
  }, [hasLoadedCategories, showDislikePopup]);

  const openDislikePopup = () => {
    setSelectedIssue("");
    setDetails("");
    setShowDislikePopup(true);
  };

  const handleLikeClick = () => {
    if (feedback === "like") return;
    setShowDislikePopup(false);
    setFeedback("like");
    onChange?.("like");
  };

  const handleDislikeClick = () => {
    if (isSubmitting) return;
    openDislikePopup();
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setShowDislikePopup(false);
  };

  const selectedCategory = issues.find((issue) => String(issue.categoryId) === selectedIssue);
  const isOtherIssueSelected = String(selectedCategory?.name || "").trim().toLowerCase() === "other";
  const isCategorySelected = Boolean(selectedIssue);
  const detailsLength = details.length;
  const isDetailsTooLong = detailsLength > FEEDBACK_DETAILS_CHAR_LIMIT;
  const isSubmitDisabled = isSubmitting
    || !isCategorySelected
    || isDetailsTooLong
    || (isOtherIssueSelected && !details.trim());

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (isDetailsTooLong) {
      return;
    }
    if (isOtherIssueSelected && !details.trim()) {
      toast.error("Please provide details for Other.");
      return;
    }

    const fallbackThreadId = window.sessionStorage.getItem(storageThreadKey) || "thread_unknown";
    const parsedCategoryId = Number(selectedCategory?.categoryId);
    const categoryId = Number.isFinite(parsedCategoryId) ? parsedCategoryId : null;
    const conversationFromStorage = window.sessionStorage.getItem(storageConversationKey);

    const payload = {
      Rating: "negative",
      CategoryId: categoryId,
      Comment: details.trim() || "",
      ThreadId: threadId || fallbackThreadId,
      AgentName: normalizedAgentName,
      UserId: userId || "unknown.user@horne.com",
      RatedMessage: ratedMessage || "N/A",
      MessageIndex: Number.isFinite(Number(messageIndex)) ? Number(messageIndex) : 1,
      ChatHistory: typeof chatHistory === "string"
        ? chatHistory
        : JSON.stringify(chatHistory || conversationFromStorage || { thread_id: threadId || fallbackThreadId }),
      UserPrompt: String(userPrompt || "").trim(),
    };

    setIsSubmitting(true);
    try {
      await submitFeedback(payload);
      // toast.dismiss(FEEDBACK_ERROR_TOAST_ID);
      toast.success("Feedback submitted successfully.", {
        toastId: FEEDBACK_SUCCESS_TOAST_ID,
        autoClose: 3000,
      });
    } catch (error) {
      // toast.dismiss(FEEDBACK_SUCCESS_TOAST_ID);
      toast.error("Failed to submit feedback. Please try again.", {
        toastId: FEEDBACK_ERROR_TOAST_ID,
        autoClose: 3000,
      });
      return;
    } finally {
      setIsSubmitting(false);
    }

    setFeedback("dislike");
    onChange?.("dislike", {
      issue: selectedCategory?.name || "",
      details: details.trim(),
    });
    setShowDislikePopup(false);
  };

  const popupContent = showDislikePopup ? (
    <div className="feedback-popup-overlay">
      <div
        className="feedback-popup"
        role="dialog"
        aria-modal="true"
        aria-label="Provide feedback"
      >
        <div className="feedback-popup-header">
          <h3>Provide Feedback</h3>
          <button type="button" className="feedback-close-btn" onClick={handleCancel} aria-label="Close feedback popup">
            ×
          </button>
        </div>

        <div className="feedback-popup-content">
          <p>What did you think about this response? *</p>

          <select
            value={selectedIssue}
            onChange={(event) => setSelectedIssue(event.target.value)}
            className="feedback-select"
            required
            disabled={isIssuesLoading || isSubmitting}
          >
            <option value="" disabled>Select an issue</option>
            {issues.map((issue) => (
              <option key={`${issue.categoryId}-${issue.name}`} value={String(issue.categoryId)}>{issue.name}</option>
            ))}
          </select>

          <textarea
            rows={5}
            placeholder={"Help us improve by sharing your thoughts (optional)"}
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            className={`feedback-textarea ${isDetailsTooLong ? "feedback-textarea-error" : ""}`}
            required={isOtherIssueSelected}
            aria-required={isOtherIssueSelected}
            aria-invalid={isDetailsTooLong}
            disabled={isSubmitting || !isCategorySelected}
          />
          <div className={`feedback-char-count ${isDetailsTooLong ? "feedback-char-count-error" : ""}`}>
            {detailsLength}
            /
            {FEEDBACK_DETAILS_CHAR_LIMIT}
          </div>
          {isDetailsTooLong && (
            <div className="feedback-validation-message" role="alert">
              Feedback cannot exceed 300 characters.
            </div>
          )}
        </div>

        <div className="feedback-popup-actions">
          <button type="button" className="feedback-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>Cancel</button>
          <button type="button" className="feedback-submit-btn" onClick={handleSubmit} disabled={isSubmitDisabled}>{isSubmitting ? "Submitting..." : "Submit"}</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <ul className="like-suggestion">
        <li>
          <button
            type="button"
            className={feedback === "like" ? "active" : ""}
            onClick={handleLikeClick}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.88235 5.49895V9.49774C3.88235 9.63031 3.83277 9.75745 3.74452 9.85119C3.65627 9.94493 3.53657 9.99759 3.41176 9.99759H2.47059C2.34578 9.99759 2.22608 9.94493 2.13783 9.85119C2.04958 9.75745 2 9.63031 2 9.49774V5.9988C2 5.86623 2.04958 5.73909 2.13783 5.64535C2.22608 5.55161 2.34578 5.49895 2.47059 5.49895H3.88235ZM3.88235 5.49895C4.38158 5.49895 4.86037 5.2883 5.21338 4.91334C5.56639 4.53838 5.76471 4.02982 5.76471 3.49955V2.9997C5.76471 2.73456 5.86387 2.48028 6.04037 2.29281C6.21688 2.10533 6.45627 2 6.70588 2C6.9555 2 7.19489 2.10533 7.37139 2.29281C7.5479 2.48028 7.64706 2.73456 7.64706 2.9997V5.49895H9.05882C9.30844 5.49895 9.54783 5.60427 9.72434 5.79175C9.90084 5.97923 10 6.23351 10 6.49865L9.52941 8.99789C9.46173 9.30454 9.33335 9.56784 9.1636 9.74815C8.99385 9.92846 8.79192 10.016 8.58824 9.99759H5.29412C4.91969 9.99759 4.56061 9.83961 4.29585 9.55839C4.03109 9.27717 3.88235 8.89575 3.88235 8.49804" stroke="#8E97A3" strokeLinecap="round" strokeLinejoin="round" fill={feedback === "like" ? "currentColor" : "none"} />
            </svg>
            Like
          </button>
        </li>

        <li>
          <button
            type="button"
            className={feedback === "dislike" ? "active" : ""}
            onClick={handleDislikeClick}
          >

            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.88235 6.50105V2.50226C3.88235 2.36969 3.83277 2.24255 3.74452 2.14881C3.65627 2.05507 3.53657 2.00241 3.41176 2.00241H2.47059C2.34578 2.00241 2.22608 2.05507 2.13783 2.14881C2.04958 2.24255 2 2.36969 2 2.50226V6.0012C2 6.13377 2.04958 6.26091 2.13783 6.35465C2.22608 6.44839 2.34578 6.50105 2.47059 6.50105H3.88235ZM3.88235 6.50105C4.38158 6.50105 4.86037 6.7117 5.21338 7.08666C5.56639 7.46162 5.76471 7.97018 5.76471 8.50045V9.0003C5.76471 9.26544 5.86387 9.51972 6.04037 9.70719C6.21688 9.89467 6.45627 10 6.70588 10C6.9555 10 7.19489 9.89467 7.37139 9.70719C7.5479 9.51972 7.64706 9.26544 7.64706 9.0003V6.50105H9.05882C9.30844 6.50105 9.54783 6.39573 9.72434 6.20825C9.90084 6.02077 10 5.76649 10 5.50135L9.52941 3.00211C9.46173 2.69546 9.33335 2.43216 9.1636 2.25185C8.99385 2.07154 8.79192 1.984 8.58824 2.00241H5.29412C4.91969 2.00241 4.56061 2.16039 4.29585 2.44161C4.03109 2.72283 3.88235 3.10425 3.88235 3.50196" stroke="#8E97A3" strokeLinecap="round" strokeLinejoin="round" fill={feedback === "dislike" ? "currentColor" : "none"} />
            </svg>
            Dislike
          </button>
        </li>
      </ul>

      {typeof document !== "undefined" ? createPortal(popupContent, document.body) : popupContent}
    </>
  );
};

export default FeedbackToggle;

FeedbackToggle.propTypes = {
  defaultValue: PropTypes.oneOf(["like", "dislike", null]),
  onChange: PropTypes.func,
  userId: PropTypes.string,
  agentName: PropTypes.string,
  threadId: PropTypes.string,
  ratedMessage: PropTypes.string,
  messageIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chatHistory: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  userPrompt: PropTypes.string,
};
